/**
 * GuidedTasks — "What to do next" panel for the project dashboard.
 * Shows prioritized actions based on the license dependency graph.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Upload, Edit3, Send, Clock, Eye, ChevronRight, Sparkles, CheckCircle, HelpCircle } from "lucide-react";
import { api } from "../../lib/api.ts";
import { status } from "../../lib/semantics.ts";
import { ErrorMessage } from "./ErrorMessage.tsx";
import { Tooltip } from "./Tooltip.tsx";

interface GuidedTasksProps {
  projectId: string;
}

interface Task {
  id: string;
  type: "fill_field" | "upload_document" | "submit_application" | "record_event" | "review_extraction" | "confirm_applicability";
  priority: number;
  title: string;
  description: string;
  licenseTypeId: string;
  licenseName: string;
  fieldId?: string;
  docType?: string;
  unlocksCount: number;
  effort: string;
  href: string;
}

// Colours + labels come from the derivation; the lucide icon stays local.
const TASK_ICONS: Record<string, any> = {
  submit_application: Send, review_extraction: Eye, upload_document: Upload,
  fill_field: Edit3, record_event: Clock, confirm_applicability: HelpCircle,
};

export function GuidedTasks({ projectId }: GuidedTasksProps) {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Stale-response guard: a fast project switch must not leave the previous
    // project's queue (or its error) on screen.
    let alive = true;
    setLoading(true);
    setError(false);
    api.getGuidedTasks(projectId)
      .then((t) => { if (alive) setTasks(t); })
      .catch(() => { if (alive) setError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [projectId, reloadKey]);

  if (loading) {
    return (
      <div className="space-y-2">
        {[1, 2, 3].map(i => <div key={i} className="h-14 bg-surface-tertiary rounded-xl animate-pulse" />)}
      </div>
    );
  }

  // A failure is never "All caught up!" — no forced green ticks.
  if (error) {
    return <ErrorMessage message="The task queue could not be loaded." onRetry={() => setReloadKey(k => k + 1)} />;
  }

  if (tasks.length === 0) {
    return (
      <div className="bg-brand-light border border-brand/20 rounded-2xl p-4 text-center">
        <CheckCircle size={20} className="text-brand mx-auto mb-2" />
        <p className="text-[15px] font-medium text-brand">All caught up!</p>
        <p className="text-[13px] text-text-secondary">No immediate actions needed. Keep going!</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-ai" />
        <h3 className="text-[13px] font-medium text-text-tertiary uppercase tracking-wider">Next steps</h3>
      </div>

      <div className="space-y-2">
        {tasks.map((task) => {
          const config = status("task", task.type);
          const Icon = TASK_ICONS[task.type] ?? Edit3;

          return (
            <Link
              key={task.id}
              to={task.href}
              className="flex items-center gap-3 px-3 py-2.5 bg-surface border border-border rounded-xl card-interactive hover:border-border-secondary no-underline text-text-primary"
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${config.bg}`}>
                <Icon size={14} className={config.text} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-medium truncate">{task.title}</div>
                <div className="text-[12px] text-text-tertiary truncate">{task.description}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Only a submission completes the prerequisite — on sub-tasks
                    the "+N" claim would be an overstatement. */}
                {task.unlocksCount > 0 && task.type === "submit_application" && (
                  <Tooltip text={`Approval unblocks ${task.unlocksCount} license${task.unlocksCount > 1 ? "s" : ""} waiting on this one`}>
                    <span className="text-[11px] text-brand bg-brand-light px-1.5 py-0.5 rounded">
                      +{task.unlocksCount}
                    </span>
                  </Tooltip>
                )}
                <span className="text-[11px] text-text-tertiary">~{task.effort}</span>
                <ChevronRight size={14} className="text-text-tertiary" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
